/**
 * Trunnion AI - Workflow List Page
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, GitBranch, Clock, Play } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { FavoriteButton } from '@/components/ui/FavoriteButton';
import { useWorkflowList, useExecuteWorkflow } from '@/hooks/queries/useWorkflows';
import { ROUTES } from '@/config/routes';
import type { WorkflowDefinition } from '@/types';

export default function WorkflowListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useWorkflowList({ search });
  const executeWorkflow = useExecuteWorkflow();

  const handleExecute = (id: string) => {
    executeWorkflow.mutate(
      { id },
      {
        onSuccess: (data) =>
          navigate(ROUTES.EXECUTION_DETAIL(data.executionId)),
      },
    );
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Workflows</h1>
          <p className="mt-1 text-sm text-trunnion-slate-400">
            Design and manage agentic workflows.
          </p>
        </div>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.WORKFLOW_NEW)}
        >
          New Workflow
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-trunnion-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search workflows..."
          className="w-full max-w-md rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 py-2 pl-9 pr-3 text-sm text-white placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500"
        />
      </div>

      {isLoading ? (
        <LoadingSpinner size="lg" label="Loading workflows..." />
      ) : data?.workflows.length === 0 ? (
        <EmptyState
          message="No workflows yet"
          description="Create your first workflow to get started with Trunnion AI."
          action={
            <Button
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(ROUTES.WORKFLOW_NEW)}
            >
              Create Workflow
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data?.workflows.map((wf: WorkflowDefinition) => (
            <Card
              key={wf.id}
              hover
              onClick={() => navigate(ROUTES.WORKFLOW_EDIT(wf.id))}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-trunnion-gold-500" />
                  <h3 className="text-sm font-semibold text-white">{wf.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <FavoriteButton type="workflow" id={wf.id} label={wf.name} />
                  <ClassificationTag level={wf.classification} />
                </div>
              </div>
              <p className="mt-2 text-xs text-trunnion-slate-400 line-clamp-2">
                {wf.description || 'No description'}
              </p>
              <div className="mt-3 flex items-center gap-3 text-xs text-trunnion-slate-400">
                <span>{wf.nodes.length} nodes</span>
                <span>{wf.edges.length} edges</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  v{wf.version}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(ROUTES.WORKFLOW_EDIT(wf.id));
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Play className="h-3.5 w-3.5" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExecute(wf.id);
                  }}
                >
                  Execute
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
